import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  useGetUserByIdAPI,
  useResendActivationMutation
} from "@/api/user/useUserAPI";
import NotificationContent from "@/components/notification/notificationContent";
import Modal from "@/components/ui/modal";
import { getDisplayErrorMessage } from "@/helpers/handleErrorResponse";
import useToast, { EToastType } from "@/hooks/ui/useToast";
import { RootState } from "@/store";
import { resetSidebarUserSettingPopupDisplay } from "@/store/slice/uiSlice";
import { DeleteUserPayload } from "@/types/user";

import AddUserPanel from "./AddUserPanel";
import EditUserPanel from "./EditUserPanel";
import ViewUserPanel from "./ViewUserPanel";

interface UserPanelProps {
  userId: string;
  onDelete: (data: DeleteUserPayload) => void;
  onEdit: () => void;
}

const UserPanel = ({ userId, onDelete, onEdit }: UserPanelProps) => {
  const dispatch = useDispatch();
  const { sidebarUserSettingPopupDisplay } = useSelector(
    (state: RootState) => state.ui
  );
  const { data: userData, error: userDetailError } = useGetUserByIdAPI(userId, {
    enabled: !!userId // On initial render, the userId can be falsy. Wait until it becomes truthy
  });
  const { showToast } = useToast();

  useEffect(() => {
    if (userDetailError) {
      showToast({
        id: "get-user-by-id-error",
        message: getDisplayErrorMessage(userDetailError),
        type: EToastType.ERROR
      });
      dispatch(resetSidebarUserSettingPopupDisplay());
    }
  }, [userDetailError, showToast, dispatch]);
  const { mutate: resendActivation } = useResendActivationMutation({
    onSuccess: () => {
      showToast({
        type: EToastType.SUCCESS,
        message: (
          <NotificationContent
            title="Verification Sent Successfully"
            description="Activation link has been sent successfully"
          />
        ),
        autoClose: 3000
      });
    },
    onError: () => {
      showToast({
        type: EToastType.ERROR,
        message: (
          <NotificationContent
            title="Something Went Wrong"
            description="Sorry, we're experiencing an issue processing 
your request. Please try again."
          />
        ),
        autoClose: 3000
      });
    }
  });

  return (
    <Modal
      isOpen={
        !!(
          sidebarUserSettingPopupDisplay?.isPanelAdd ||
          // Wait for the user detail so a failed request doesn't flash an empty modal
          ((sidebarUserSettingPopupDisplay?.isPanelView ||
            sidebarUserSettingPopupDisplay?.isPanelEdit) &&
            userData)
        )
      }
      onClose={() => resetSidebarUserSettingPopupDisplay()}
      containerClassname="!static"
    >
      {sidebarUserSettingPopupDisplay?.isPanelAdd && <AddUserPanel />}
      {sidebarUserSettingPopupDisplay?.isPanelEdit && userData && (
        <EditUserPanel userData={userData} userId={userId} />
      )}
      {sidebarUserSettingPopupDisplay?.isPanelView && userData ? (
        <ViewUserPanel
          userData={userData}
          onEdit={onEdit}
          onResendActivationClick={() => {
            if (userId) {
              resendActivation(userId);
            }
          }}
          onDelete={() =>
            onDelete({ id: userId, fullname: userData.full_name })
          }
        />
      ) : null}
    </Modal>
  );
};

export default UserPanel;
